import { forwardRef, useMemo } from "react";

import { Formik, Form as FormikForm } from "formik";
import { noop } from "neetocist";
import PropTypes from "prop-types";

import {
  DEFAULT_HOUR_FORMAT,
  DEFAULT_SLOT_INTERVAL,
  HOUR_FORMATS,
} from "./constants";
import DisplayAvailability from "./DisplayAvailability";
import Fields from "./Fields";
import {
  buildSlotIntervals,
  slotsSelectComponentDefaultValue,
  filterSlots,
  generateNewPeriod,
  addDefaultPeriod,
  isValidTimeSlotOption,
  formatTimeSlotOption,
  formatCreateLabel,
} from "./Form/utils";
import Header from "./Header";
import "./schedule.scss";
import ScheduleContext from "./ScheduleContext";
import {
  buildFormValuesArray,
  buildFormValuesObject,
  getScheduleValidationSchema,
  processFormValues,
} from "./utils";

const Schedule = forwardRef(
  (
    {
      periods,
      handleSubmit = noop,
      isEditing = false,
      setIsEditing,
      showHeader = true,
      handleValuesChanged = noop,
      allowAddPeriod = true,
      isEditable = true,
      isCreating = false,
      firstDay = 1,
      showTimeZone = true,
      slotInterval = DEFAULT_SLOT_INTERVAL,
      hourFormat = DEFAULT_HOUR_FORMAT,
    },
    scheduleRef
  ) => {
    const validationSchema = useMemo(getScheduleValidationSchema, []);

    const getInitialFormValues = (isSubmitHandler = false) => {
      if (isSubmitHandler) {
        return buildFormValuesArray({ firstDay, periods });
      }

      return buildFormValuesObject({ periods });
    };

    return (
      <ScheduleContext.Provider
        value={{ allowAddPeriod, firstDay, slotInterval, hourFormat }}
      >
        <div className="neeto-molecules-schedule neeto-ui-border-gray-200 flex-shrink-0">
          <Formik
            {...{ validationSchema }}
            enableReinitialize
            initialValues={{ wdays: getInitialFormValues() }}
            onSubmit={values => {
              handleSubmit(
                processFormValues(values, firstDay),
                getInitialFormValues(true)
              );
            }}
          >
            <>
              {showHeader && (
                <Header
                  {...{
                    isCreating,
                    isEditable,
                    isEditing,
                    setIsEditing,
                    showTimeZone,
                  }}
                />
              )}
              {isEditing || !showHeader ? (
                <FormikForm>
                  <Fields {...{ handleValuesChanged, scheduleRef }} />
                </FormikForm>
              ) : (
                <DisplayAvailability {...{ periods }} />
              )}
            </>
          </Formik>
        </div>
      </ScheduleContext.Provider>
    );
  }
);

Schedule.displayName = "Schedule";

Schedule.propTypes = {
  /**
   * Periods data for days of the week.
   */
  periods: PropTypes.shape({
    sunday: PropTypes.array,
    monday: PropTypes.array,
    tuesday: PropTypes.array,
    wednesday: PropTypes.array,
    thursday: PropTypes.array,
    friday: PropTypes.array,
    saturday: PropTypes.array,
  }),
  /**
   * The function to be called on save changes action.
   */
  handleSubmit: PropTypes.func,
  /**
   * State value to show form for editing.
   */
  isEditing: PropTypes.bool,
  /**
   * Setter function to toggle `isEditing` state.
   */
  setIsEditing: PropTypes.func,
  /**
   * Flag to display or hide header. If set to false, component will open in editing state.
   */
  showHeader: PropTypes.bool,
  /**
   * Callback function called when values are changed within form.
   */
  handleValuesChanged: PropTypes.func,
  /**
   * Flag to allow adding new periods. If set to false, Add new period button will be hidden.
   */
  allowAddPeriod: PropTypes.bool,
  /**
   * Flag to control the ability to edit the schedule and timezone. When set to false, periods and timezone cannot be edited.
   */
  isEditable: PropTypes.bool,
  /**
   * Flag to control the ability to submit the schedule without changing initial values. When set to false, Schedule can only be submitted once the initial values are changed.
   */
  isCreating: PropTypes.bool,
  /**
   * Integer prop to choose which day of the week to display first in the component. Defaults to 1(Monday).
   */
  firstDay: PropTypes.number,
  /**
   * Flag to display or hide the current timezone in the header. Defaults to true.
   */
  showTimeZone: PropTypes.bool,
  /**
   * Integer prop to specify the time (in minutes) taken by each slot. Defaults to 30 minutes.
   */
  slotInterval: PropTypes.number,
  /**
   * String prop to specify the hour format (24h/12h). Defaults to 24h.
   */
  hourFormat: PropTypes.oneOf(Object.values(HOUR_FORMATS)),
};

export default Schedule;

export {
  buildSlotIntervals,
  slotsSelectComponentDefaultValue,
  filterSlots,
  generateNewPeriod,
  addDefaultPeriod,
  isValidTimeSlotOption,
  formatTimeSlotOption,
  formatCreateLabel,
};
